import React, { useState, useEffect } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../types/kundali';
import { AdvancedLifeAnalysisResult } from '../types/advancedLifeAnalysis';
import { BrihatJanmapatrikaReport, DossierScope, PrintTheme } from './BrihatJanmapatrikaReport';
import {
  getSavedAstrologerBranding,
  saveAstrologerBranding,
  AstrologerBranding
} from '../lib/astrologerBrandingService';
import { SUPPORTED_LANGUAGES } from '../constants/languages';
import { downloadElementAsPdf, PaperSize, PAPER_DIMENSIONS } from '../utils/pdfExportUtil';
import { HtmlPrintPreviewModal } from './HtmlPrintPreviewModal';
import {
  X,
  Printer,
  Download,
  Globe,
  UserCheck,
  Edit3,
  Check,
  Sparkles,
  ExternalLink,
  Loader2,
  AlertCircle,
  Briefcase,
  Heart,
  Shield,
  FileText,
  BookOpen,
  Layers,
  Palette
} from 'lucide-react';

interface PrintReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  kundali: CompleteKundali;
  analysisResult?: AdvancedLifeAnalysisResult;
  reportMode?: 'BASIC' | 'LEARNER' | 'PRO' | 'ACHARYA';
  lang?: Language;
}

export const PrintReportModal: React.FC<PrintReportModalProps> = ({
  isOpen,
  onClose,
  kundali,
  reportMode = 'PRO',
  lang: initialLang = 'hi'
}) => {
  // Pre-print configuration states
  const [selectedLang, setSelectedLang] = useState<Language>(initialLang || 'hi');
  const [reportScope, setReportScope] = useState<DossierScope>('BRIHAT');
  const [activeGrantha, setActiveGrantha] = useState<number | 'ALL'>('ALL');
  const [paperSize, setPaperSize] = useState<PaperSize>('a4');
  const [printTheme, setPrintTheme] = useState<PrintTheme>('deluxe');
  const [chartStyle, setChartStyle] = useState<ChartStyle>('north');

  // Persistent Astrologer Profile
  const [branding, setBranding] = useState<AstrologerBranding>(() => getSavedAstrologerBranding());
  const [isEditingBranding, setIsEditingBranding] = useState<boolean>(false);
  const [brandingForm, setBrandingForm] = useState<AstrologerBranding>(() => getSavedAstrologerBranding());
  const [saveSuccessNotice, setSaveSuccessNotice] = useState<string | null>(null);

  // PDF Export & HTML Print Tool States
  const [isExportingPdf, setIsExportingPdf] = useState<boolean>(false);
  const [pdfProgressText, setPdfProgressText] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isHtmlPreviewOpen, setIsHtmlPreviewOpen] = useState<boolean>(false);

  // Sync initial language if passed
  useEffect(() => {
    if (initialLang) {
      setSelectedLang(initialLang);
    }
  }, [initialLang]);

  if (!isOpen) return null;

  // Handle saving astrologer profile (persists to localStorage & settings forever)
  const handleSaveBranding = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const saved = saveAstrologerBranding(brandingForm);
    setBranding(saved);
    setIsEditingBranding(false);
    setSaveSuccessNotice('Branding profile saved successfully.');
    setTimeout(() => setSaveSuccessNotice(null), 3000);
  };

  const handleClearBranding = () => {
    const blank: AstrologerBranding = {
      name: '',
      title: '',
      sansthan: '',
      location: '',
      phone: '',
      customHeaderSlogan: '',
      email: '',
      registrationNumber: ''
    };
    const saved = saveAstrologerBranding(blank);
    setBranding(saved);
    setBrandingForm(saved);
    setIsEditingBranding(false);
    setSaveSuccessNotice('All branding cleared. Reports will remain blank and neutral.');
    setTimeout(() => setSaveSuccessNotice(null), 3000);
  };

  const getCleanReportTitle = () => {
    const cleanName = (kundali.birthDetails?.name || 'Native').replace(/[^a-zA-Z0-9_\u0900-\u097F]/g, '_');
    const granthaTag = activeGrantha === 'ALL' ? 'All_Granthas' : `Grantha_${activeGrantha}`;
    const scopeTag = reportScope === 'GRAND' ? `Bhrigu_Samhita_${granthaTag}` : reportScope === 'BRIHAT' ? 'Brihat_Patrika' : `${reportScope}_Dossier`;
    const themeTag = printTheme === 'eco-bw' ? 'EcoBW' : 'Deluxe';
    const paperTag = paperSize.toUpperCase();
    return `${cleanName}_${scopeTag}_${paperTag}_${themeTag}`;
  };

  // 1. Direct PDF File Export (.pdf file generated and saved to device)
  const handleDownloadPdf = async () => {
    setIsExportingPdf(true);
    setErrorMessage(null);
    const filename = getCleanReportTitle();

    try {
      await downloadElementAsPdf(
        'brihat-janmapatrika-container',
        filename,
        (msg) => setPdfProgressText(msg),
        paperSize
      );
      setSaveSuccessNotice('PDF dossier downloaded successfully to your device!');
      setTimeout(() => setSaveSuccessNotice(null), 4000);
    } catch (err: any) {
      console.error('PDF download error:', err);
      setErrorMessage(
        'Direct canvas download encountered a browser limit. You can use "Open in Printable Tab" or "System Print" below to save as PDF.'
      );
    } finally {
      setIsExportingPdf(false);
      setPdfProgressText('');
    }
  };

  // 2. Open In-App HTML Print Tool (Replaces popup tab to avoid popup blockers)
  const handleOpenHtmlPreview = () => {
    setIsHtmlPreviewOpen(true);
  };

  // 3. Output to Physical Printer / Native Print Dialog
  const handlePhysicalPrint = () => {
    const originalTitle = document.title;
    document.title = getCleanReportTitle();
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  return (
    <div
      id="print-report-modal-overlay"
      className="fixed inset-0 z-50 bg-[#121E1C]/75 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 overflow-y-auto animate-fadeIn"
      onClick={onClose}
    >
      <div
        id="print-report-modal-content"
        className="bg-astro-bg border border-astro-border rounded-3xl w-full max-w-6xl max-h-[96vh] flex flex-col shadow-2xl overflow-hidden animate-scaleUp print:p-0 print:m-0 print:border-none print:shadow-none print:max-w-none print:max-h-none print:h-auto print:overflow-visible"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ================================================================= */}
        {/* TOP CONFIGURATION & ACTION BAR (HIDDEN WHEN PRINTING)             */}
        {/* ================================================================= */}
        <div className="px-5 py-3.5 bg-astro-card border-b border-astro-border flex flex-wrap items-center justify-between gap-3 shrink-0 print:hidden">
          {/* Title & Native Summary */}
          <div className="flex items-center gap-2.5">
            <span className="text-xs font-bold uppercase tracking-wider bg-astro-primary text-white px-2.5 py-1 rounded-lg flex items-center gap-1 shadow-2xs">
              <Sparkles className="w-3 h-3 text-astro-accent" />
              <span>वृहत् जन्मपत्रिका</span>
            </span>
            <h2 className="text-sm sm:text-base font-bold font-serif text-astro-text truncate max-w-xs sm:max-w-md">
              {kundali.birthDetails?.name ? `${kundali.birthDetails.name} — Publication Dossier` : 'Brihat Janmapatrika'}
            </h2>
          </div>

          {/* Quick Action Buttons */}
          <div className="flex items-center flex-wrap gap-2">
            {/* 1. Direct PDF Download */}
            <button
              onClick={handleDownloadPdf}
              disabled={isExportingPdf}
              className="px-3.5 py-2 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer active:scale-95 disabled:opacity-60"
              title="Generate and download actual .pdf file directly"
            >
              {isExportingPdf ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-astro-accent" />
                  <span>Generating PDF...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 text-astro-accent" />
                  <span>Download PDF</span>
                </>
              )}
            </button>

            {/* 2. Open In-App HTML Print Tool */}
            <button
              onClick={handleOpenHtmlPreview}
              className="px-3.5 py-2 rounded-xl bg-astro-secondary-surface hover:bg-astro-card border border-astro-border text-astro-text text-xs font-bold flex items-center gap-1.5 transition-all shadow-2xs cursor-pointer active:scale-95"
              title="Open in-app HTML print preview tool"
            >
              <ExternalLink className="w-3.5 h-3.5 text-astro-primary" />
              <span className="hidden sm:inline">HTML Print Tool</span>
              <span className="sm:hidden">Print Tool</span>
            </button>

            {/* 3. Send to Physical Printer */}
            <button
              onClick={handlePhysicalPrint}
              className="px-3 py-2 rounded-xl bg-astro-secondary-surface hover:bg-astro-card border border-astro-border text-astro-secondary-text hover:text-astro-text text-xs font-semibold flex items-center gap-1.5 transition-all shadow-2xs cursor-pointer active:scale-95"
              title="Send directly to system printer dialog"
            >
              <Printer className="w-3.5 h-3.5 text-astro-muted" />
              <span className="hidden md:inline">Print</span>
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-astro-bg border border-astro-border hover:bg-astro-secondary-surface text-astro-text transition-colors cursor-pointer min-h-[36px] min-w-[36px] flex items-center justify-center ml-1"
              aria-label="Close report dialog"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Loading Progress Bar */}
        {isExportingPdf && (
          <div className="bg-astro-secondary text-white px-4 py-2 text-xs font-semibold flex items-center justify-center gap-2 animate-fadeIn border-b border-[#B58A4A] print:hidden">
            <Loader2 className="w-4 h-4 animate-spin text-astro-accent" />
            <span>{pdfProgressText || 'Rendering high-resolution PDF pages... Please wait.'}</span>
          </div>
        )}

        {/* Error Notice */}
        {errorMessage && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-900 px-4 py-2.5 text-xs flex items-center justify-between gap-3 animate-fadeIn print:hidden">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
              <span>{errorMessage}</span>
            </div>
            <button
              onClick={handleOpenHtmlPreview}
              className="px-2.5 py-1 rounded bg-amber-700 text-white text-[11px] font-bold hover:bg-amber-800 cursor-pointer shrink-0"
            >
              HTML Print Tool
            </button>
          </div>
        )}

        {/* ================================================================= */}
        {/* PRE-PRINT CONTROL BAR: LANGUAGE, BRANDING & SCOPE                 */}
        {/* ================================================================= */}
        <div className="px-5 py-2.5 bg-astro-secondary-surface border-b border-astro-border flex flex-wrap items-center justify-between gap-3 shrink-0 text-xs print:hidden">
          {/* 1. Language Selector Dropdown (All App Languages Supported) */}
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-astro-primary shrink-0" />
            <span className="font-semibold text-astro-secondary-text hidden sm:inline">Language:</span>
            <select
              value={selectedLang}
              onChange={(e) => setSelectedLang(e.target.value as Language)}
              className="px-2.5 py-1.5 rounded-lg border border-astro-border bg-astro-bg font-semibold text-astro-text focus:outline-none focus:ring-2 focus:ring-astro-primary cursor-pointer shadow-2xs text-xs"
            >
              {SUPPORTED_LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.native} ({l.label})
                </option>
              ))}
            </select>
          </div>

          {/* 2. Paper Size Selector (Default A4, supports Letter, Legal, A3 with auto-resizing) */}
          <div className="flex items-center gap-1.5 bg-astro-bg px-2.5 py-1 rounded-lg border border-astro-border text-astro-text shadow-2xs">
            <Printer className="w-3.5 h-3.5 text-astro-primary shrink-0" />
            <span className="font-bold text-[11px] text-astro-secondary-text">Paper:</span>
            <select
              value={paperSize}
              onChange={(e) => setPaperSize(e.target.value as PaperSize)}
              className="bg-transparent font-bold text-astro-primary text-xs focus:outline-none cursor-pointer"
            >
              <option value="a4">A4 (210×297 mm - Standard)</option>
              <option value="letter">US Letter (8.5×11")</option>
              <option value="legal">US Legal (8.5×14")</option>
              <option value="a3">A3 Deluxe (297×420 mm)</option>
            </select>
          </div>

          {/* 3. Astrologer Profile Quick Pill & Edit Trigger */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 bg-astro-bg px-2.5 py-1 rounded-lg border border-astro-border text-astro-text shadow-2xs">
              <UserCheck className="w-3.5 h-3.5 text-astro-primary" />
              <span className="truncate max-w-[140px] sm:max-w-[200px] font-medium">
                {branding.name || branding.sansthan || 'Astrologer: Blank / Not Set'}
              </span>
              <button
                onClick={() => setIsEditingBranding(!isEditingBranding)}
                className="text-[10px] text-astro-primary hover:underline font-bold ml-1 cursor-pointer flex items-center gap-0.5"
                title="Edit Astrologer Branding (Remains saved permanently)"
              >
                <Edit3 className="w-3 h-3 text-astro-accent" />
                <span>{isEditingBranding ? 'Close' : 'Set/Edit'}</span>
              </button>
            </div>

            {/* Scope / Dossier Preset Toggle */}
            <div className="flex items-center gap-1 bg-astro-bg p-0.5 rounded-lg border border-astro-border flex-wrap">
              <button
                onClick={() => setReportScope('GRAND')}
                className={`px-2.5 py-1 rounded text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  reportScope === 'GRAND'
                    ? 'bg-amber-600 text-white shadow-2xs'
                    : 'text-amber-800 hover:text-amber-900 bg-amber-50/70 border border-amber-200'
                }`}
                title="Eco-Compact Master Edition (~56 pages): 100% data density, saves printer paper, saves ink, zero empty space"
              >
                <Sparkles className="w-3 h-3 text-amber-300" />
                <span>{selectedLang === 'hi' ? 'महा-संहिता (~५६ पृष्ठ)' : 'Eco-Master (~56 pgs)'}</span>
              </button>
              <button
                onClick={() => setReportScope('BRIHAT')}
                className={`px-2.5 py-1 rounded text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  reportScope === 'BRIHAT' ? 'bg-astro-primary text-white shadow-2xs' : 'text-astro-secondary-text hover:text-astro-text'
                }`}
                title="Standard Dossier (20 pages): The distilled client consultation dossier"
              >
                <BookOpen className="w-3 h-3" />
                <span>{selectedLang === 'hi' ? 'वृहत् पत्रिका (२० पृष्ठ)' : 'Standard Dossier (20 pgs)'}</span>
              </button>
              <button
                onClick={() => setReportScope('SUMMARY')}
                className={`px-2 py-1 rounded text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  reportScope === 'SUMMARY' ? 'bg-astro-primary text-white shadow-2xs' : 'text-astro-secondary-text hover:text-astro-text'
                }`}
                title="Executive Summary (4 pages): Quick reference sheet"
              >
                <FileText className="w-3 h-3" />
                <span>{selectedLang === 'hi' ? 'संक्षिप्त (४ पृष्ठ)' : 'Exec Summary (4 pgs)'}</span>
              </button>
              <button
                onClick={() => setReportScope('CAREER')}
                className={`px-2 py-1 rounded text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  reportScope === 'CAREER' ? 'bg-astro-primary text-white shadow-2xs' : 'text-astro-secondary-text hover:text-astro-text'
                }`}
                title="Career & Wealth Dossier (D10, 2nd/10th/11th Bhava analysis, Ashtakavarga, 20-Year Career Forecast)"
              >
                <Briefcase className="w-3 h-3" />
                <span>{selectedLang === 'hi' ? 'कर्म व अर्थ' : 'Career'}</span>
              </button>
              <button
                onClick={() => setReportScope('MARRIAGE')}
                className={`px-2 py-1 rounded text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  reportScope === 'MARRIAGE' ? 'bg-astro-primary text-white shadow-2xs' : 'text-astro-secondary-text hover:text-astro-text'
                }`}
                title="Marriage & Relationship Dossier (D9, 7th Bhava, Manglik Dosha, Sade Sati, Kalatra Saukhyam)"
              >
                <Heart className="w-3 h-3" />
                <span>{selectedLang === 'hi' ? 'विवाह व दाम्पत्य' : 'Marriage'}</span>
              </button>
              <button
                onClick={() => setReportScope('REMEDIAL')}
                className={`px-2 py-1 rounded text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  reportScope === 'REMEDIAL' ? 'bg-astro-primary text-white shadow-2xs' : 'text-astro-secondary-text hover:text-astro-text'
                }`}
                title="Lal Kitab & Remedial Dossier (7 Ancestral Debts, 43-Day Totkes, Gemstones, Herbal Baths)"
              >
                <Shield className="w-3 h-3" />
                <span>{selectedLang === 'hi' ? 'लाल किताब व उपाय' : 'Remedies'}</span>
              </button>
            </div>

            {/* B&W Eco-Print vs Deluxe Gold Theme Switch */}
            <div className="flex items-center gap-1 bg-astro-bg p-0.5 rounded-lg border border-astro-border">
              <button
                onClick={() => setPrintTheme('deluxe')}
                className={`px-2.5 py-1 rounded text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  printTheme === 'deluxe' ? 'bg-amber-700 text-white shadow-2xs' : 'text-astro-secondary-text hover:text-astro-text'
                }`}
                title="Full Color / Deluxe Gold Parchment Theme (Optimal for screen view & digital PDFs)"
              >
                <Palette className="w-3 h-3 text-amber-300" />
                <span>{selectedLang === 'hi' ? 'स्वर्ण / रंगीन' : 'Deluxe'}</span>
              </button>
              <button
                onClick={() => setPrintTheme('eco-bw')}
                className={`px-2.5 py-1 rounded text-[11px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  printTheme === 'eco-bw' ? 'bg-black text-white shadow-2xs' : 'text-astro-secondary-text hover:text-astro-text'
                }`}
                title="B&W Eco-Print (Ink Saver) — Pure white background, zero color fills, saves printer ink!"
              >
                <Printer className="w-3 h-3" />
                <span>{selectedLang === 'hi' ? 'स्याही बचत (B&W)' : 'B&W (Save Ink)'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* ================================================================= */}
        {/* GRANTHA SELECTOR TABS (SHOWN WHEN IN GRAND COMPENDIUM MODE)       */}
        {/* ================================================================= */}
        {reportScope === 'GRAND' && (
          <div className="w-full flex items-center gap-1.5 overflow-x-auto py-2 px-4 bg-amber-50/90 border-b border-amber-200 text-xs shrink-0 print:hidden">
            <span className="font-bold text-amber-950 shrink-0 flex items-center gap-1 mr-1">
              <Sparkles className="w-3.5 h-3.5 text-amber-600" />
              <span>{selectedLang === 'hi' ? 'महा-ग्रन्थ चयन:' : 'Master Grantha:'}</span>
            </span>
            {[
              { id: 'ALL', label: selectedLang === 'hi' ? 'समस्त ८ ग्रन्थ (संपूर्ण महा-संहिता)' : 'All 8 Granthas (Full Book)' },
              { id: 1, label: selectedLang === 'hi' ? 'ग्रन्थ १: जन्म गणित' : 'G1: Janma Ganita' },
              { id: 2, label: selectedLang === 'hi' ? 'ग्रन्थ २: षोडशवर्ग मण्डल' : 'G2: Shodashvarga' },
              { id: 3, label: selectedLang === 'hi' ? 'ग्रन्थ ३: षड्बल व अष्टकवर्ग' : 'G3: Bala & Ashtakavarga' },
              { id: 4, label: selectedLang === 'hi' ? 'ग्रन्थ ४: बहु-दशा महाचक्र' : 'G4: Multi-Dasha' },
              { id: 5, label: selectedLang === 'hi' ? 'ग्रन्थ ५: ग्रह-भाव फलित' : 'G5: Shastric Phaladesh' },
              { id: 6, label: selectedLang === 'hi' ? 'ग्रन्थ ६: लाल किताब ऋण व फरमान' : 'G6: Lal Kitab' },
              { id: 7, label: selectedLang === 'hi' ? 'ग्रन्थ ७: २०-वर्षीय वर्षफल' : 'G7: 20-Yr Varshaphala' },
              { id: 8, label: selectedLang === 'hi' ? 'ग्रन्थ ८: रत्न, योग व मङ्गलम्' : 'G8: Gemology & Colophon' }
            ].map((g) => (
              <button
                key={g.id}
                onClick={() => setActiveGrantha(g.id as any)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold whitespace-nowrap transition-all cursor-pointer ${
                  activeGrantha === g.id
                    ? 'bg-amber-700 text-white shadow-xs'
                    : 'bg-white hover:bg-amber-100 text-amber-900 border border-amber-300'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        )}

        {/* ================================================================= */}
        {/* COLLAPSIBLE ASTROLOGER BRANDING EDITOR (SAVES PERMANENTLY)        */}
        {/* ================================================================= */}
        {isEditingBranding && (
          <div className="bg-astro-card border-b-2 border-astro-border p-4 shrink-0 animate-fadeIn print:hidden">
            <form onSubmit={handleSaveBranding} className="max-w-4xl mx-auto space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-astro-primary" />
                  <h3 className="font-bold text-xs text-astro-text uppercase tracking-wider">
                    Astrologer Branding & Institution Credentials (Optional)
                  </h3>
                </div>
                <span className="text-[10px] text-astro-primary bg-astro-teal-tint px-2 py-0.5 rounded border border-astro-border font-semibold">
                  Leave fields empty to keep reports completely unbranded
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 text-xs">
                <div>
                  <label className="block text-[10px] font-bold text-astro-secondary-text mb-0.5">Acharya / Astrologer Name:</label>
                  <input
                    type="text"
                    value={brandingForm.name}
                    onChange={(e) => setBrandingForm({ ...brandingForm, name: e.target.value })}
                    className="w-full px-2.5 py-1.5 bg-astro-bg border border-astro-border rounded-lg text-astro-text font-semibold text-xs focus:ring-1 focus:ring-astro-primary"
                    placeholder="Enter name or leave blank"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-astro-secondary-text mb-0.5">Title / Qualifications:</label>
                  <input
                    type="text"
                    value={brandingForm.title}
                    onChange={(e) => setBrandingForm({ ...brandingForm, title: e.target.value })}
                    className="w-full px-2.5 py-1.5 bg-astro-bg border border-astro-border rounded-lg text-astro-text text-xs focus:ring-1 focus:ring-astro-primary"
                    placeholder="e.g. Jyotish Acharya (Optional)"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-astro-secondary-text mb-0.5">Sansthan / Practice Name:</label>
                  <input
                    type="text"
                    value={brandingForm.sansthan}
                    onChange={(e) => setBrandingForm({ ...brandingForm, sansthan: e.target.value })}
                    className="w-full px-2.5 py-1.5 bg-astro-bg border border-astro-border rounded-lg text-astro-text text-xs focus:ring-1 focus:ring-astro-primary"
                    placeholder="Enter institution or leave blank"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-astro-secondary-text mb-0.5">Location / City:</label>
                  <input
                    type="text"
                    value={brandingForm.location}
                    onChange={(e) => setBrandingForm({ ...brandingForm, location: e.target.value })}
                    className="w-full px-2.5 py-1.5 bg-astro-bg border border-astro-border rounded-lg text-astro-text text-xs focus:ring-1 focus:ring-astro-primary"
                    placeholder="e.g. City, State (Optional)"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-astro-secondary-text mb-0.5">WhatsApp / Phone Number:</label>
                  <input
                    type="text"
                    value={brandingForm.phone}
                    onChange={(e) => setBrandingForm({ ...brandingForm, phone: e.target.value })}
                    className="w-full px-2.5 py-1.5 bg-astro-bg border border-astro-border rounded-lg text-astro-text font-mono text-xs focus:ring-1 focus:ring-astro-primary"
                    placeholder="Phone / WhatsApp (Optional)"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-astro-secondary-text mb-0.5">Header Slogan (Auspicious Motto):</label>
                  <input
                    type="text"
                    value={brandingForm.customHeaderSlogan || ''}
                    onChange={(e) => setBrandingForm({ ...brandingForm, customHeaderSlogan: e.target.value })}
                    className="w-full px-2.5 py-1.5 bg-astro-bg border border-astro-border rounded-lg text-astro-text text-xs focus:ring-1 focus:ring-astro-primary"
                    placeholder="e.g. वेद चक्षुः समं ज्योतिः (Optional)"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={handleClearBranding}
                  className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold cursor-pointer transition-colors"
                >
                  Clear All / Keep Blank
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsEditingBranding(false)}
                    className="px-3 py-1.5 rounded-lg border border-astro-border text-astro-secondary-text text-xs font-semibold hover:bg-astro-secondary-surface cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-1.5 rounded-lg bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center gap-1 shadow-xs cursor-pointer"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>Save Branding Profile</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Save confirmation toast */}
        {saveSuccessNotice && (
          <div className="bg-astro-primary text-white text-xs font-bold text-center py-1.5 shadow-sm animate-fadeIn print:hidden">
            ✓ {saveSuccessNotice}
          </div>
        )}

        {/* ================================================================= */}
        {/* REPORT BODY: BRIHAT JANMAPATRIKA PUBLICATION ENGINE                */}
        {/* ================================================================= */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-astro-secondary-surface/40 print:p-0 print:bg-astro-bg print:overflow-visible">
          <BrihatJanmapatrikaReport
            kundali={kundali}
            lang={selectedLang}
            branding={branding}
            chartStyle={chartStyle}
            reportScope={reportScope}
            paperSize={paperSize}
            printTheme={printTheme}
            activeGrantha={activeGrantha}
          />
        </div>
      </div>
      <HtmlPrintPreviewModal
        isOpen={isHtmlPreviewOpen}
        onClose={() => setIsHtmlPreviewOpen(false)}
        elementId="brihat-janmapatrika-container"
        documentTitle={getCleanReportTitle()}
        paperSize={paperSize}
      />
    </div>
  );
};
